import { Link } from "react-router-dom";
import { ArrowRight, Target, Eye, Sparkles, Zap, GraduationCap, Camera, TrendingUp } from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "../components/motion";
import { CTAPanel } from "../components/CTAPanel";

const AI_POINTS = [
  {
    icon: Zap,
    title: "Days, not months",
    text: "AI drafts your website content, social posts and customer replies in hours — then real humans polish every detail. Agency quality at small-business speed.",
  },
  {
    icon: Sparkles,
    title: "A team member that never sleeps",
    text: "AI chatbots answer your customers at 2pm and 2am, capture leads while you're on the tools, and only tap you on the shoulder when a human is truly needed.",
  },
  {
    icon: TrendingUp,
    title: "Big-business tools, small-business prices",
    text: "Automation that used to cost enterprises six figures — follow-ups, invoicing, reporting — now runs quietly in the background of your business for the price of a weekly coffee run.",
  },
];

export default function About() {
  return (
    <>
      <section className="pt-[72px] hero-grid-bg" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-20 pb-16 sm:pt-28 sm:pb-24 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <p className="text-indigo font-semibold text-sm uppercase tracking-widest mb-4">About Avora</p>
            <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-ink leading-[1.06]">
              More than an agency. Your long-term <span className="text-gradient">digital partner</span>.
            </h1>
            <p className="mt-6 text-slate text-base sm:text-lg leading-relaxed">
              Avora is more than a web design agency — we become the digital department for your small business. Websites, branding, content, ads and automation: one team that knows your business and handles every digital need as it grows. No juggling five freelancers, no explaining your business from scratch every time.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative">
              <div className="rounded-[2rem] overflow-hidden shadow-[0_24px_64px_rgba(20,22,46,0.15)]">
                <img
                  src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=1200&q=80"
                  alt="A happy client after a great service experience"
                  className="w-full h-[320px] sm:h-[420px] object-cover"
                  data-testid="about-client-image"
                />
              </div>
              <div className="absolute -bottom-6 -left-4 sm:-left-8 bg-white rounded-2xl shadow-[0_16px_40px_rgba(20,22,46,0.14)] border border-ink/5 px-6 py-4">
                <p className="font-display font-bold text-ink text-lg">Sydney, NSW</p>
                <p className="text-slate text-xs">serving businesses Australia-wide</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="px-5 sm:px-8 py-24 bg-white" data-testid="founder-story">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-[1fr_1.4fr] gap-14 items-start">
          <Reveal>
            <div className="lg:sticky lg:top-28">
              <div className="rounded-[2rem] overflow-hidden shadow-[0_24px_64px_rgba(20,22,46,0.12)] mb-6">
                <img
                  src="https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=1200&q=80"
                  alt="The Avora team brainstorming in the office"
                  className="w-full h-[340px] object-cover"
                  data-testid="team-image"
                />
              </div>
              <p className="font-display font-semibold text-ink">One team for everything digital</p>
              <p className="text-slate text-sm">Strategy, design, code, content & marketing — together</p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-indigo font-semibold text-sm uppercase tracking-widest mb-4">The founder's story</p>
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-ink leading-[1.12] mb-8">
              "I always wanted the one-stop shop. So I built it."
            </h2>
            <div className="space-y-5 text-ink/75 text-base leading-relaxed">
              <p>
                Avora's founder graduated with a Bachelor of Computer Engineering and went on to work across business, e-commerce, videography, photography and digital marketing — seeing the online world from every angle: the code, the camera, the ads, and the balance sheet.
              </p>
              <p>
                Along the way, one frustration kept coming back. Getting a business properly online meant hiring a designer here, a developer there, a photographer somewhere else — then playing project manager trying to connect them all. Every business owner he worked with said the same thing: "I just want one place that does everything."
              </p>
              <p>
                So that became the idea behind Avora: a genuine one-stop shop where branding, websites, content, ads and automation are designed to work together from day one — one team, one relationship, zero runaround. When AI made it possible to deliver agency-quality work at small-business speed and prices, Avora was born.
              </p>
              <div className="flex flex-wrap gap-3 pt-2">
                {[
                  { icon: GraduationCap, label: "Computer Engineering graduate" },
                  { icon: Camera, label: "Videography & photography" },
                  { icon: TrendingUp, label: "E-commerce & digital marketing" },
                ].map((b) => (
                  <span key={b.label} className="inline-flex items-center gap-2 bg-cloud border border-ink/8 rounded-full px-4 py-2 text-xs font-medium text-ink/75">
                    <b.icon className="w-3.5 h-3.5 text-indigo" /> {b.label}
                  </span>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="px-5 sm:px-8 py-24" data-testid="mission-vision">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-5">
          <Reveal>
            <div className="h-full bg-ink grain rounded-[2rem] p-10 sm:p-12 relative overflow-hidden" data-testid="mission-card">
              <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-indigo/25 blur-[90px]" />
              <div className="relative z-10">
                <span className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center mb-6">
                  <Target className="w-5 h-5 text-mint" />
                </span>
                <p className="text-mint font-semibold text-sm uppercase tracking-widest mb-3">Our mission</p>
                <p className="font-display font-semibold text-white text-xl sm:text-2xl leading-snug">
                  Make a professional digital presence accessible and affordable for every small business — not just the ones with big budgets.
                </p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="h-full bg-gradient-to-br from-indigo to-mint rounded-[2rem] p-10 sm:p-12" data-testid="vision-card">
              <span className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center mb-6">
                <Eye className="w-5 h-5 text-white" />
              </span>
              <p className="text-white/80 font-semibold text-sm uppercase tracking-widest mb-3">Our vision</p>
              <p className="font-display font-semibold text-white text-xl sm:text-2xl leading-snug">
                A world where every business owner spends their time growing their business — and leaves the digital work to us.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="px-5 sm:px-8 py-24 bg-white" data-testid="how-ai-helps">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <p className="text-indigo font-semibold text-sm uppercase tracking-widest mb-4">How AI helps your business</p>
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-ink max-w-2xl leading-[1.1] mb-12">
              We use AI so you get more, for less.
            </h2>
          </Reveal>
          <Stagger className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {AI_POINTS.map((p) => (
              <StaggerItem key={p.title}>
                <div data-testid={`ai-point-${p.title.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="h-full bg-cloud rounded-3xl border border-ink/5 p-8">
                  <span className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo/10 to-mint/10 flex items-center justify-center mb-5">
                    <p.icon className="w-5 h-5 text-indigo" />
                  </span>
                  <h3 className="font-display font-semibold text-ink text-lg mb-2.5">{p.title}</h3>
                  <p className="text-slate text-sm leading-relaxed">{p.text}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="px-5 sm:px-8 py-24" data-testid="why-different">
        <Reveal className="max-w-5xl mx-auto text-center">
          <p className="text-indigo font-semibold text-sm uppercase tracking-widest mb-6">Why we're different</p>
          <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-ink leading-[1.12]">
            Most agencies sell you a website and wave goodbye. Most freelancers do one thing well. <span className="text-gradient">Avora does it all</span> — launch, grow, automate — with AI speed, human craft, and prices a small business can actually afford.
          </h2>
          <p className="mt-8 text-slate text-base sm:text-lg">One team. One invoice. Zero runaround.</p>
          <Link to="/contact" data-testid="about-cta" className="mt-10 inline-flex items-center gap-2 bg-ink text-white font-semibold px-8 py-4 rounded-full hover:bg-indigo transition-colors duration-300 group">
            Come say hello
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </section>

      <CTAPanel title="Let's build yours next." />
    </>
  );
}

